import { useState, useRef } from 'react';
import {
  Button, ButtonGroup, Popper, Grow, Paper,
  ClickAwayListener, MenuList, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  ArrowDropDown as ArrowDropDownIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { EXPORT_COLORS, getExportColors } from '@/theme/colorTokens';

 
type AnyRow = Record<string, any>;

export interface ExportColumn {
  field: string;
  headerName: string;
  /** Optional value getter — receives the row and returns the display value */
  valueGetter?: (row: AnyRow) => string | number;
}

interface ExportMenuProps {
  /** Columns to export (field + headerName). Exclude action columns. */
  columns: ExportColumn[];
  /** Row data to export — accepts any array of objects */
   
  rows: any[];
  /** Base filename without extension (e.g. "expenses-2025") */
  filename: string;
  /** Optional title shown at the top of PDF */
  title?: string;
  /** Size variant */
  size?: 'small' | 'medium';
}

function getCellValue(row: AnyRow, col: ExportColumn): string {
  if (col.valueGetter) {
    const v = col.valueGetter(row);
    return v == null ? '' : String(v);
  }
  const v = row[col.field];
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function buildTableData(rows: AnyRow[], columns: ExportColumn[]) {
  const headers = columns.map((c) => c.headerName);
  const body = rows.map((row) => columns.map((col) => getCellValue(row, col)));
  return { headers, body };
}

async function exportPdf(
  rows: AnyRow[],
  columns: ExportColumn[],
  filename: string,
  title?: string,
  colors = EXPORT_COLORS,
) {
  const [{ jsPDF }, autoTableMod] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  const autoTable = autoTableMod.default;
  const { headers, body } = buildTableData(rows, columns);
  const doc = new jsPDF({ orientation: 'landscape' });

  if (title) {
    doc.setFontSize(16);
    doc.text(title, 14, 18);
  }

  autoTable(doc, {
    head: [headers],
    body,
    startY: title ? 26 : 14,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: colors.headerBg, textColor: colors.headerText, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: colors.altRowBg },
    margin: { left: 14, right: 14 },
  });

  doc.save(`${filename}.pdf`);
}

async function exportExcel(
  rows: AnyRow[],
  columns: ExportColumn[],
  filename: string,
  title?: string,
) {
  const [XLSX, { saveAs }] = await Promise.all([
    import('xlsx'),
    import('file-saver'),
  ]);
  const { headers, body } = buildTableData(rows, columns);
  const wsData = [headers, ...body];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Auto-size columns
  ws['!cols'] = headers.map((h, i) => {
    const maxLen = Math.max(h.length, ...body.map((r) => (r[i] || '').length));
    return { wch: Math.min(maxLen + 2, 40) };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title || 'Data');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

function handlePrint(
  rows: AnyRow[],
  columns: ExportColumn[],
  title?: string,
  colors = EXPORT_COLORS,
) {
  const { headers, body } = buildTableData(rows, columns);

  const html = `
    <html>
    <head>
      <title>${title || 'Print'}</title>
      <style>
        body { font-family: Inter, Roboto, Arial, sans-serif; padding: 20px; color: ${colors.printText}; }
        h2 { margin-bottom: 12px; color: ${colors.printHeading}; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th { background: ${colors.printHeaderBg}; color: ${colors.printHeaderText}; padding: 8px 10px; text-align: left; font-weight: 600; }
        td { padding: 6px 10px; border-bottom: 1px solid ${colors.printBorder}; }
        tr:nth-child(even) { background: ${colors.printAltRow}; }
        @media print {
          body { padding: 0; }
          @page { margin: 15mm; }
        }
      </style>
    </head>
    <body>
      ${title ? `<h2>${title}</h2>` : ''}
      <table>
        <thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
        <tbody>${body.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join('')}</tr>`).join('')}</tbody>
      </table>
    </body>
    </html>
  `;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
    win.onload = () => {
      win.print();
      win.close();
    };
  }
}

export default function ExportMenu({ columns, rows, filename, title, size = 'small' }: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const paletteColors = getExportColors(theme.palette.primary.main, theme.palette.background.default);

  const handleAction = (action: 'pdf' | 'excel' | 'print') => {
    setOpen(false);
    if (rows.length === 0) return;

    switch (action) {
      case 'pdf':
        void exportPdf(rows, columns, filename, title, paletteColors);
        break;
      case 'excel':
        void exportExcel(rows, columns, filename, title);
        break;
      case 'print':
        handlePrint(rows, columns, title, paletteColors);
        break;
    }
  };

  return (
    <>
      <ButtonGroup variant="outlined" size={size} ref={anchorRef} sx={{ '& .MuiButton-root': { textTransform: 'none' } }}>
        <Button startIcon={<DownloadIcon />} onClick={() => handleAction('pdf')}>
          Export
        </Button>
        <Button size={size} onClick={() => setOpen((prev) => !prev)} sx={{ px: 0.5, minWidth: 'auto' }}>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper open={open} anchorEl={anchorRef.current} transition disablePortal sx={{ zIndex: 1300 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={4}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList dense>
                  <MenuItem onClick={() => handleAction('pdf')}>
                    <ListItemIcon><PdfIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Export as PDF</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleAction('excel')}>
                    <ListItemIcon><ExcelIcon fontSize="small" color="success" /></ListItemIcon>
                    <ListItemText>Export as Excel</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleAction('print')}>
                    <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Print</ListItemText>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
